'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface TeamMember {
  id: string;
  name: string;
  capacity: number; // hours per sprint
  vacationDays: number;
  onCallDays: number;
}

interface SprintConfig {
  sprintLengthDays: number;
  workingHoursPerDay: number;
  meetingHoursPerDay: number;
  supportOverheadPercent: number;
}

export default function SprintCapacityPlannerPage() {
  // Team Members (free tier limited to 8)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    { id: '1', name: '', capacity: 80, vacationDays: 0, onCallDays: 0 },
  ]);

  // Sprint Configuration
  const [sprintConfig, setSprintConfig] = useState<SprintConfig>({
    sprintLengthDays: 14,
    workingHoursPerDay: 8,
    meetingHoursPerDay: 1.5,
    supportOverheadPercent: 15,
  });

  // Historical Data
  const [historicalVelocity, setHistoricalVelocity] = useState<number>(45);
  const [averageStoryPoints, setAverageStoryPoints] = useState<number>(3);
  const [plannedStoryPoints, setPlannedStoryPoints] = useState<number>(50);

  // Command Center state
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'sprint-capacity-planner',
    toolCategory: 'planner',
  });

  // Calculations
  const calculations = useMemo(() => {
    const validMembers = teamMembers.filter(m => m.name.trim());

    // Calculate total capacity
    const totalTheoretical = validMembers.reduce((sum, m) => {
      const totalDays = sprintConfig.sprintLengthDays;
      const availableDays = totalDays - m.vacationDays - m.onCallDays;
      const hoursPerDay = sprintConfig.workingHoursPerDay - sprintConfig.meetingHoursPerDay;
      const totalHours = availableDays * hoursPerDay;
      const afterOverhead = totalHours * (1 - sprintConfig.supportOverheadPercent / 100);
      return sum + afterOverhead;
    }, 0);

    // Individual capacities
    const memberCapacities = validMembers.map(m => {
      const totalDays = sprintConfig.sprintLengthDays;
      const availableDays = totalDays - m.vacationDays - m.onCallDays;
      const hoursPerDay = sprintConfig.workingHoursPerDay - sprintConfig.meetingHoursPerDay;
      const totalHours = availableDays * hoursPerDay;
      const afterOverhead = totalHours * (1 - sprintConfig.supportOverheadPercent / 100);
      return {
        name: m.name,
        hours: afterOverhead,
        percent: (afterOverhead / totalTheoretical) * 100,
      };
    });

    // Velocity prediction
    const capacityBasedVelocity = (totalTheoretical / 8) * 1.5; // Rough estimate
    const predictedVelocity = (historicalVelocity * 0.7) + (capacityBasedVelocity * 0.3);

    // Story completion
    const estimatedStories = Math.floor(plannedStoryPoints / averageStoryPoints);
    const velocityRatio = (plannedStoryPoints / predictedVelocity) * 100;

    // Burn-down prediction (simplified)
    const daysInSprint = sprintConfig.sprintLengthDays;
    const pointsPerDay = predictedVelocity / daysInSprint;
    const burnDown = Array.from({ length: daysInSprint + 1 }, (_, i) => ({
      day: i,
      ideal: Math.max(0, plannedStoryPoints - (pointsPerDay * i)),
      predicted: Math.max(0, plannedStoryPoints - (pointsPerDay * i * 0.9)), // 10% buffer
    }));

    // WIP limit recommendation (Little's Law approximation)
    const wipLimit = Math.ceil(validMembers.length * 1.5);

    // Sprint health
    const isOverCommitted = velocityRatio > 120;
    const isUnderCommitted = velocityRatio < 70;
    const healthStatus = isOverCommitted ? 'Over-committed' :
                         isUnderCommitted ? 'Under-committed' : 'Healthy';

    return {
      totalTheoretical,
      memberCapacities,
      predictedVelocity,
      estimatedStories,
      velocityRatio,
      burnDown,
      wipLimit,
      healthStatus,
      isOverCommitted,
      isUnderCommitted,
    };
  }, [teamMembers, sprintConfig, historicalVelocity, averageStoryPoints, plannedStoryPoints]);

  // Team member functions
  const addTeamMember = () => {
    if (teamMembers.length >= 8) {
      alert('Free tier limited to 8 team members. Upgrade to Pro for unlimited members.');
      return;
    }
    setTeamMembers([
      ...teamMembers,
      { id: Date.now().toString(), name: '', capacity: 80, vacationDays: 0, onCallDays: 0 },
    ]);
  };

  const updateTeamMember = (id: string, field: keyof TeamMember, value: string | number) => {
    setTeamMembers(teamMembers.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  const removeTeamMember = (id: string) => {
    setTeamMembers(teamMembers.filter(m => m.id !== id));
  };

  // Export to Excel
  const exportToExcel = () => {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => {
      const XLSX = (window as any).XLSX;
      const wb = XLSX.utils.book_new();

      // Sheet 1: Sprint Summary
      const summaryData = [
        ['Sprint Capacity Plan', ''],
        ['', ''],
        ['Sprint Configuration', ''],
        ['Sprint Length (days)', sprintConfig.sprintLengthDays],
        ['Working Hours/Day', sprintConfig.workingHoursPerDay],
        ['Meeting Hours/Day', sprintConfig.meetingHoursPerDay],
        ['Support Overhead', sprintConfig.supportOverheadPercent + '%'],
        ['', ''],
        ['Capacity', ''],
        ['Total Capacity (hours)', calculations.totalTheoretical.toFixed(1)],
        ['Predicted Velocity (points)', calculations.predictedVelocity.toFixed(1)],
        ['Historical Velocity (points)', historicalVelocity],
        ['', ''],
        ['Sprint Plan', ''],
        ['Planned Story Points', plannedStoryPoints],
        ['Average Story Size (points)', averageStoryPoints],
        ['Estimated Stories', calculations.estimatedStories],
        ['Commitment Ratio', calculations.velocityRatio.toFixed(0) + '%'],
        ['Sprint Health', calculations.healthStatus],
        ['', ''],
        ['Recommendations', ''],
        ['WIP Limit', calculations.wipLimit + ' stories'],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Sheet 2: Team Capacity
      const teamData = calculations.memberCapacities.map(m => ({
        'Team Member': m.name,
        'Capacity (hours)': m.hours.toFixed(1),
        'Capacity (%)': m.percent.toFixed(1) + '%',
      }));

      const ws2 = XLSX.utils.json_to_sheet(teamData);
      ws2['!cols'] = [{ wch: 20 }, { wch: 18 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Team Capacity');

      // Sheet 3: Burn-down Chart Data
      const burnDownData = calculations.burnDown.map(d => ({
        'Day': d.day,
        'Ideal Remaining': d.ideal.toFixed(1),
        'Predicted Remaining': d.predicted.toFixed(1),
      }));

      const ws3 = XLSX.utils.json_to_sheet(burnDownData);
      ws3['!cols'] = [{ wch: 8 }, { wch: 18 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Burn-down');

      (wb.Workbook as any) = { CalcPr: { fullCalcOnLoad: true } };
      XLSX.writeFile(wb, 'sprint-capacity-plan.xlsx');
    };
    document.head.appendChild(script);
  };

  const getHealthColor = () => {
    if (calculations.isOverCommitted) return 'text-red-500';
    if (calculations.isUnderCommitted) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getHealthBgColor = () => {
    if (calculations.isOverCommitted) return 'bg-red-500/10 border-red-500/30';
    if (calculations.isUnderCommitted) return 'bg-yellow-500/10 border-yellow-500/30';
    return 'bg-green-500/10 border-green-500/30';
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const validMembers = teamMembers.filter(m => m.name.trim());
    if (validMembers.length === 0) return;

    const memberCapacityScores: Record<string, number> = {};
    calculations.memberCapacities.forEach(m => {
      memberCapacityScores[m.name] = m.percent;
    });

    const recommendations = [];
    if (calculations.isOverCommitted) {
      recommendations.push({
        id: 'over-committed',
        title: 'Reduce Sprint Scope',
        priority: 'high' as const,
        description: `Sprint is over-committed at ${calculations.velocityRatio.toFixed(0)}%. Consider reducing scope or extending sprint.`,
      });
    }
    if (calculations.isUnderCommitted) {
      recommendations.push({
        id: 'under-committed',
        title: 'Add More Stories',
        priority: 'medium' as const,
        description: `Sprint is under-committed at ${calculations.velocityRatio.toFixed(0)}%. Consider adding more stories.`,
      });
    }
    recommendations.push({
      id: 'wip-limit',
      title: `Maintain WIP Limit of ${calculations.wipLimit}`,
      priority: 'medium' as const,
      description: 'Keep active stories under this limit to maintain flow.',
    });

    const assessment = await saveResult({
      name: `Sprint Capacity Plan - ${new Date().toLocaleDateString()}`,
      score_overall: Math.min(100, Math.max(0, 100 - Math.abs(100 - calculations.velocityRatio))),
      scores: memberCapacityScores,
      answers: {
        sprintConfig,
        teamMembers: validMembers.map(m => ({ name: m.name, vacationDays: m.vacationDays, onCallDays: m.onCallDays })),
        historicalVelocity,
        averageStoryPoints,
        plannedStoryPoints,
      },
      recommendations,
      metadata: {
        totalCapacityHours: calculations.totalTheoretical,
        predictedVelocity: calculations.predictedVelocity,
        estimatedStories: calculations.estimatedStories,
        velocityRatio: calculations.velocityRatio,
        wipLimit: calculations.wipLimit,
        healthStatus: calculations.healthStatus,
        teamSize: validMembers.length,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/tools" className="text-accent hover:text-accent mb-4 inline-block">
            ← Back to Tools
          </Link>
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-foreground">
            Sprint Capacity Planner
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
            Calculate team capacity accounting for vacation, meetings, and overhead. Predict velocity,
            generate burn-down charts, and get WIP limit recommendations.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Configuration */}
          <div className="space-y-6">
            {/* Sprint Configuration */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Sprint Configuration
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Sprint Length (days)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={sprintConfig.sprintLengthDays}
                    onChange={(e) => setSprintConfig({ ...sprintConfig, sprintLengthDays: Math.max(1, parseInt(e.target.value) || 14) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Working Hours per Day
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    step="0.5"
                    value={sprintConfig.workingHoursPerDay}
                    onChange={(e) => setSprintConfig({ ...sprintConfig, workingHoursPerDay: Math.max(1, parseFloat(e.target.value) || 8) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Meeting Hours per Day
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    step="0.5"
                    value={sprintConfig.meetingHoursPerDay}
                    onChange={(e) => setSprintConfig({ ...sprintConfig, meetingHoursPerDay: Math.max(0, parseFloat(e.target.value) || 1.5) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    Standup, retro, planning, etc.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Support Overhead (%)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="5"
                    value={sprintConfig.supportOverheadPercent}
                    onChange={(e) => setSprintConfig({ ...sprintConfig, supportOverheadPercent: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-light-text-secondary dark:text-foreground-tertiary">
                    <span>0%</span>
                    <span className="font-semibold text-light-text dark:text-foreground">
                      {sprintConfig.supportOverheadPercent}%
                    </span>
                    <span>50%</span>
                  </div>
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    Bug fixes, support tickets, code reviews
                  </p>
                </div>
              </div>
            </div>

            {/* Team Members */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-light-text dark:text-foreground">
                  Team Members ({teamMembers.filter(m => m.name.trim()).length})
                </h2>
                <button
                  onClick={addTeamMember}
                  className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors text-sm"
                >
                  + Add Member
                </button>
              </div>

              <div className="space-y-3">
                {teamMembers.map((member, index) => (
                  <div key={member.id} className="border border-gray-300 dark:border-border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <input
                        type="text"
                        placeholder="Team member name"
                        value={member.name}
                        onChange={(e) => updateTeamMember(member.id, 'name', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                      />
                      {teamMembers.length > 1 && (
                        <button
                          onClick={() => removeTeamMember(member.id)}
                          className="ml-2 text-red-500 hover:text-red-600"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          Vacation Days
                        </label>
                        <input
                          type="number" inputMode="numeric"
                          min="0"
                          value={member.vacationDays}
                          onChange={(e) => updateTeamMember(member.id, 'vacationDays', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          On-Call Days
                        </label>
                        <input
                          type="number" inputMode="numeric"
                          min="0"
                          value={member.onCallDays}
                          onChange={(e) => updateTeamMember(member.id, 'onCallDays', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {teamMembers.length >= 8 && (
                <div className="mt-3 text-sm text-accent">
                  ⚠️ Free tier limited to 8 members. Upgrade to Pro for unlimited.
                </div>
              )}
            </div>

            {/* Historical Data */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Velocity & Planning
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Historical Velocity (story points)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={historicalVelocity}
                    onChange={(e) => setHistoricalVelocity(Math.max(1, parseInt(e.target.value) || 45))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    Average from last 3 sprints
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Average Story Size (points)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    step="0.5"
                    value={averageStoryPoints}
                    onChange={(e) => setAverageStoryPoints(Math.max(0.5, parseFloat(e.target.value) || 3))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Planned Story Points
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={plannedStoryPoints}
                    onChange={(e) => setPlannedStoryPoints(Math.max(1, parseInt(e.target.value) || 50))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Results */}
          <div className="space-y-6">
            {/* Sprint Health */}
            <div className={`border rounded-lg p-6 ${getHealthBgColor()}`}>
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Sprint Health
              </h2>

              <div className="text-center mb-6">
                <div className={`text-4xl font-bold mb-2 ${getHealthColor()}`}>
                  {calculations.healthStatus}
                </div>
                <div className="text-light-text-secondary dark:text-foreground-secondary">
                  Commitment Ratio: {calculations.velocityRatio.toFixed(0)}%
                </div>
              </div>

              {calculations.isOverCommitted && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-500 font-semibold">
                    ⚠️ Over-committed: Consider reducing scope or extending sprint
                  </p>
                </div>
              )}

              {calculations.isUnderCommitted && (
                <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-600 dark:text-yellow-500 font-semibold">
                    ⚠️ Under-committed: Consider adding more stories
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Total Capacity
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.totalTheoretical.toFixed(0)}h
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Predicted Velocity
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.predictedVelocity.toFixed(0)} pts
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Estimated Stories
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.estimatedStories}
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    WIP Limit
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.wipLimit}
                  </div>
                </div>
              </div>
            </div>

            {/* Team Capacity Breakdown */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Team Capacity Breakdown
              </h2>

              {calculations.memberCapacities.length > 0 ? (
                <div className="space-y-3">
                  {calculations.memberCapacities.map((member, idx) => (
                    <div key={idx} className="flex justify-between items-center pb-3 border-b border-gray-300 dark:border-border last:border-0">
                      <span className="text-light-text dark:text-foreground font-medium">{member.name}</span>
                      <div className="text-right">
                        <div className="text-lg font-bold text-light-text dark:text-foreground">
                          {member.hours.toFixed(1)}h
                        </div>
                        <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">
                          {member.percent.toFixed(0)}% of total
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-light-text-secondary dark:text-foreground-secondary">
                  Add team members to see capacity breakdown
                </p>
              )}
            </div>

            {/* Burn-down Preview */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Burn-down Chart Preview
              </h2>

              <div className="relative h-48 bg-gray-100 dark:bg-background rounded-lg p-4">
                {/* Simple ASCII-style chart */}
                <div className="h-full flex flex-col justify-between">
                  {[0, 25, 50, 75, 100].map((percent) => (
                    <div key={percent} className="flex items-center gap-2">
                      <span className="text-xs text-light-text-secondary dark:text-foreground-tertiary w-8">
                        {Math.round(plannedStoryPoints * (1 - percent / 100))}
                      </span>
                      <div className="flex-1 border-t border-gray-300 dark:border-border"></div>
                    </div>
                  ))}
                </div>

                <div className="absolute bottom-4 left-12 right-4 flex justify-between text-xs text-light-text-secondary dark:text-foreground-tertiary">
                  <span>Day 0</span>
                  <span>Day {Math.floor(sprintConfig.sprintLengthDays / 2)}</span>
                  <span>Day {sprintConfig.sprintLengthDays}</span>
                </div>
              </div>

              <p className="text-xs text-light-text-secondary dark:text-foreground-secondary mt-3">
                Export to Excel for full burn-down chart with daily projections
              </p>
            </div>

            {/* Recommendations */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Recommendations
              </h2>

              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-accent">•</span>
                  <p className="text-light-text-secondary dark:text-foreground-secondary">
                    <span className="font-semibold text-light-text dark:text-foreground">WIP Limit:</span>{' '}
                    Keep active stories under {calculations.wipLimit} to maintain flow
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-accent">•</span>
                  <p className="text-light-text-secondary dark:text-foreground-secondary">
                    <span className="font-semibold text-light-text dark:text-foreground">Buffer:</span>{' '}
                    Reserve 10-20% capacity for unplanned work
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-accent">•</span>
                  <p className="text-light-text-secondary dark:text-foreground-secondary">
                    <span className="font-semibold text-light-text dark:text-foreground">Story Size:</span>{' '}
                    Keep stories between 1-8 points for better predictability
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-accent">•</span>
                  <p className="text-light-text-secondary dark:text-foreground-secondary">
                    <span className="font-semibold text-light-text dark:text-foreground">Daily Review:</span>{' '}
                    Update burn-down daily during standup
                  </p>
                </div>
              </div>
            </div>

            {/* Export */}
            <button
              onClick={exportToExcel}
              className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
            >
              📊 Export Sprint Plan to Excel
            </button>

            {/* Command Center Save Button */}
            {calculations.memberCapacities.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-4">
                {commandCenterEnabled && !saved && (
                  <button
                    onClick={handleSaveToCommandCenter}
                    disabled={saving}
                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save to Command Center'}
                  </button>
                )}
                {saved && (
                  <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Saved to Command Center
                  </div>
                )}
                {saveError && (
                  <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                    {saveError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pro Features */}
        <div className="mt-8 bg-gradient-to-r from-accent-muted to-accent/5 border-l-4 border-accent p-6 rounded-lg">
          <h3 className="text-xl font-bold text-accent mb-3">⭐ Unlock Pro Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Pro ($29/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Multi-team capacity planning</li>
                <li>✓ Historical velocity tracking</li>
                <li>✓ WIP limit recommendations</li>
                <li>✓ Excel export with charts</li>
                <li>✓ Sprint goal tracker</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Team ($99/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Everything in Pro</li>
                <li>✓ Jira/Linear data import</li>
                <li>✓ Multi-team dependency mapping</li>
                <li>✓ Predictive sprint success analytics</li>
                <li>✓ Executive sprint summary</li>
              </ul>
            </div>
          </div>
          <Link
            href="/pricing"
            className="inline-block px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
          >
            Upgrade to Pro →
          </Link>
        </div>
      </div>
    </div>
  );
}
